import LabeledSlider from '../../LabeledSlider';
import {
  Anchor,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconCopy, IconDeviceFloppy, IconMail, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  appCallToActionButtonOptions,
  appStoresOptions,
  attributionSpecOptions,
  callToActionButtonOptions,
  optimizationGoalOptions as globalOptimizationGoalOptions,
  objectiveOptions,
} from '../../../constants/FacebookAd';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import LockableButton from '../../Lockable/LockableButton';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import Spinner from '../../Spinner';
import WarningMessageOnDelete from '../../WarningMessageOnDelete';
import FacebookAdFormImage from './FacebookAdFormImage';
import FacebookAdFormPlacement from './FacebookAdFormPlacement';

const FacebookAdForm = ({
  facebookAd: passedFacebookAd,
  saveFacebookAd,
  deleteFacebookAd,
  onBack,
  onDuplicate,
  showUserFlow,
}) => {
  const [facebookAd, setFacebookAd] = useState(passedFacebookAd);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [durationOption, setDurationOption] = useState();
  const [attributionSpecSelected, setAttributionSpecSelected] = useState('7dayClick1dayView');
  const [deleteError, setDeleteError] = useState();
  const [productSets, setProductSets] = useState();
  const [adAccountCurrencySymbol, setAdAccountCurrencySymbol] = useState();
  const [businessOwnedApps, setBusinessOwnedApps] = useState([]);
  const [error, setError] = useState();
  const { accountId, domainId } = useParams();
  const fetch = useFetch();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/productSets`)
      .then(({ facebookProductSets }) => {
        setProductSets(facebookProductSets.filter((set) => set.status));
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/account`)
      .then(({ facebookAccount }) => {
        if (!facebookAccount) {
          return;
        }
        const currency = facebookAccount.adAccountCurrency;
        const symbol = Intl.NumberFormat(navigator.language, { style: 'currency', currency })
          .formatToParts(1)
          .find((x) => x.type === 'currency').value;
        setAdAccountCurrencySymbol(symbol);
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/businessOwnedApps`)
      .then((res) => {
        if (res?.businessOwnedApps?.length) {
          setBusinessOwnedApps(
            res.businessOwnedApps.map((b) => ({ value: b.id, label: `${b.name} (${b.id}) - ${b.link}` })),
          );
        }
      })
      .catch(setError);

    if (facebookAd.durationInDays === null) {
      setDurationOption('unlimitedTime');
    } else {
      setDurationOption('timeFramed');
    }
  }, [accountId, domainId, fetch, facebookAd.durationInDays]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (productSets === undefined) {
    return <Spinner />;
  }

  const objective = objectiveOptions.find(({ value }) => facebookAd.objective === value);

  let optimizationGoalOptions = [];
  if (objective) {
    optimizationGoalOptions = objective.optimizationGoalOptions
      .map((id) => globalOptimizationGoalOptions.find((goal) => goal.id === id))
      .filter(Boolean);
  }

  const deleteButton = deleteFacebookAd ? (
    <LockableButton restrictionKey="facebookAdsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconTrash size={14} /> Delete
    </LockableButton>
  ) : undefined;

  const duplicateButton =
    facebookAd.id && onDuplicate ? (
      <LockableButton restrictionKey="facebookAdsCreate" color="cyan" onClick={onDuplicate}>
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    ) : undefined;

  const productSetOptions = productSets.map((set) => ({ label: set.name, value: set.id.toString() }));

  const handleDurationOptionChange = (option) => {
    setDurationOption(option);
    if (option === 'unlimitedTime') {
      setFacebookAd({ ...facebookAd, durationInDays: null });
    }
  };

  const handlePromotedVisualChange = (value) => {
    if (value === 'image') {
      setFacebookAd({ ...facebookAd, promotedVisual: value, facebookProductSetId: null, showProductName: false, showProductPrice: false, showProductDescription: false });
    } else if (value === 'carousel') {
      setFacebookAd({ ...facebookAd, promotedVisual: value, imageUrl: null, showProductName: true, showProductPrice: true, showProductDescription: true });
    }
  };

  const callToActionOptions =
    facebookAd.objective === 'OUTCOME_APP_PROMOTION' ? appCallToActionButtonOptions : callToActionButtonOptions;

  const handleAttributionSpecChange = (value) => {
    setAttributionSpecSelected(value);
    setFacebookAd({ ...facebookAd, attributionSpec: value });
  };
  const canAttributeObjective =
    facebookAd.objective === 'OUTCOME_ENGAGEMENT' ||
    facebookAd.objective === 'OUTCOME_LEADS' ||
    facebookAd.objective === 'OUTCOME_SALES'
      ? true
      : false;

  return (
    <Card withBorder>
      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Name
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <TextInput
            name="name"
            value={facebookAd.name}
            placeholder="e.g. Idle Customers"
            onChange={(e) => setFacebookAd({ ...facebookAd, name: e.target.value })}
          />
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Message
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Textarea
            rows={5}
            onChange={(e) => setFacebookAd({ ...facebookAd, message: e.target.value })}
            name="message"
            value={facebookAd.message}
          />
          <Text size="xs" c="dimmed">
            This is the text that appears on top of the ad.
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Objective
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Select
            data={objectiveOptions}
            onChange={(value) => {
              setFacebookAd({
                ...facebookAd,
                objective: value,
                bidAmount: facebookAd.bidAmount,
                callToAction: value === 'OUTCOME_ENGAGEMENT' ? null : facebookAd.callToAction,
                attributionSpec:
                  value === 'OUTCOME_ENGAGEMENT' || value === 'OUTCOME_LEADS' || value === 'OUTCOME_SALES'
                    ? attributionSpecSelected
                    : null,
                appStore: null,
                appMessage: null,
                appUrl: null,
                optimizationGoal: null,
              });
            }}
            value={facebookAd.objective}
            disabled={facebookAd.id}
          />
          <Text size="xs" c="dimmed">
            The campaign's objective. See{' '}
            <Anchor href="https://www.facebook.com/business/ads/ad-objectives" target="_blank" rel="noreferrer">
              this link
            </Anchor>{' '}
            for details.
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Optimization Goal
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Select
            data={optimizationGoalOptions}
            onChange={(value) => {
              setFacebookAd({
                ...facebookAd,
                optimizationGoal: value,
              });
            }}
            value={facebookAd.optimizationGoal}
            disabled={facebookAd.id}
          />
          <Text size="xs" c="dimmed">
            This tells Facebook what result you care about most, so it can show your ads to people most likely to take
            that action.
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Call to Action Button
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <Select
            data={callToActionOptions}
            onChange={(value) => {
              setFacebookAd({ ...facebookAd, callToAction: value });
            }}
            value={facebookAd.callToAction}
            disabled={facebookAd.id || facebookAd.objective === 'OUTCOME_ENGAGEMENT'}
          />
          <Text size="xs" c="dimmed">
            The ad's call to action button. See{' '}
            <Anchor
              href="https://www.facebook.com/business/help/450688792208401?ref=search_new_3"
              target="_blank"
              rel="noreferrer"
            >
              this link
            </Anchor>{' '}
            for details.
          </Text>
        </Grid.Col>
      </Grid>

      {facebookAd.objective === 'OUTCOME_APP_PROMOTION' ? (
        <>
          <Grid mb="sm">
            <Grid.Col span={3}>
              <Text size="sm" fw={500}>
                Call to action message
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <TextInput
                name="appMessage"
                value={facebookAd.appMessage}
                placeholder="e.g. Install our app, Download our app etc."
                onChange={(e) => setFacebookAd({ ...facebookAd, appMessage: e.target.value })}
                disabled={facebookAd.id}
              />
              <Text size="xs" c="dimmed">
                This is the text that appears before the button of the ad.
              </Text>
            </Grid.Col>
          </Grid>

          <Grid mb="sm">
            <Grid.Col span={3}>
              <Text size="sm" fw={500}>
                App platform
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <Select
                data={appStoresOptions}
                onChange={(value) => {
                  setFacebookAd({
                    ...facebookAd,
                    appStore: value,
                  });
                }}
                value={facebookAd.appStore}
                disabled={facebookAd.id}
              />
            </Grid.Col>
          </Grid>

          <Grid mb="sm">
            <Grid.Col span={3}>
              <Text size="sm" fw={500}>
                App Store URL
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <TextInput
                name="appUrl"
                value={facebookAd.appUrl}
                placeholder="Link to your app in the store"
                onChange={(e) => setFacebookAd({ ...facebookAd, appUrl: e.target.value })}
                disabled={facebookAd.id}
              />
              <Text size="xs" c="dimmed">
                App URL link. See{' '}
                <Anchor
                  href="https://www.facebook.com/business/help/1627795160837067?id=1858550721111595"
                  target="_blank"
                  rel="noreferrer"
                >
                  this link
                </Anchor>{' '}
                for details.
              </Text>
            </Grid.Col>
          </Grid>

          <Grid mb="sm">
            <Grid.Col span={3}>
              <Text size="sm" fw={500}>
                Facebook App
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <Select
                data={businessOwnedApps}
                onChange={(value) => {
                  setFacebookAd({ ...facebookAd, appId: value });
                }}
                value={facebookAd.appId}
                disabled={facebookAd.id}
              />
              <Text size="xs" c="dimmed">
                If you don't have a facebook app, please create one and associate it with your app. See{' '}
                <Anchor href="https://developers.facebook.com/quickstarts/" target="_blank" rel="noreferrer">
                  this link
                </Anchor>{' '}
                for details.
              </Text>
            </Grid.Col>
          </Grid>
        </>
      ) : null}

      {facebookAd.objective ? <FacebookAdFormPlacement facebookAd={facebookAd} setFacebookAd={setFacebookAd} /> : null}

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Promoted Visual
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <SegmentedControl
            value={facebookAd.promotedVisual ?? ''}
            onChange={handlePromotedVisualChange}
            data={[
              { label: 'Products Carousel', value: 'carousel' },
              { label: 'Image', value: 'image' },
            ]}
            disabled={!!facebookAd.id}
          />
          <Text size="xs" c="dimmed">
            Select your media type of your facebook ad.
          </Text>
        </Grid.Col>
      </Grid>

      {facebookAd.promotedVisual === 'carousel' ? (
        <>
          <Grid mb="sm">
            <Grid.Col span={3}>
              <Text size="sm" fw={500}>
                Promoted Products
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <Select
                disabled={facebookAd.id}
                data={productSetOptions}
                onChange={(value) => setFacebookAd({ ...facebookAd, facebookProductSetId: value })}
                value={facebookAd.facebookProductSetId?.toString()}
              />
              <Text size="xs" c="dimmed">
                This controls the set of products that facebook will consider when choosing which products to show in
                the ad for each user. This will show below the ad text.
              </Text>
            </Grid.Col>
          </Grid>

          <Grid mb="sm">
            <Grid.Col span={3}>
              <Text size="sm" fw={500}>
                Product Details
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <SegmentedControl
                value={String(
                  facebookAd.showProductName || facebookAd.showProductPrice || facebookAd.showProductDescription
                )}
                onChange={(value) => {
                  const showDetails = value === 'true';
                  setFacebookAd({
                    ...facebookAd,
                    showProductName: showDetails,
                    showProductPrice: showDetails,
                    showProductDescription: showDetails,
                  });
                }}
                data={[
                  { label: 'Show Details', value: 'true' },
                  { label: 'Do Not Show Details', value: 'false' },
                ]}
              />
              {(facebookAd.showProductName || facebookAd.showProductPrice || facebookAd.showProductDescription) && (
                <Group mt="xs">
                  <Checkbox
                    label="Product Name"
                    checked={facebookAd.showProductName}
                    onChange={(event) => setFacebookAd({ ...facebookAd, showProductName: event.currentTarget.checked })}
                  />
                  <Checkbox
                    label="Product Price"
                    checked={facebookAd.showProductPrice}
                    onChange={(event) => setFacebookAd({ ...facebookAd, showProductPrice: event.currentTarget.checked })}
                  />
                  <Checkbox
                    label="Product Description"
                    checked={facebookAd.showProductDescription}
                    onChange={(event) => setFacebookAd({ ...facebookAd, showProductDescription: event.currentTarget.checked })}
                  />
                </Group>
              )}
              <Text size="xs" c="dimmed" mt="xs">
                Customize your Facebook Ad by choosing which product details to display in the carousel cards.
              </Text>
            </Grid.Col>
          </Grid>
        </>
      ) : (
        <FacebookAdFormImage facebookAd={facebookAd} setFacebookAd={setFacebookAd} />
      )}

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Attribution
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          {canAttributeObjective && (
            <SegmentedControl
              disabled={!!facebookAd.id || canAttributeObjective === false}
              value={attributionSpecSelected ?? ''}
              onChange={handleAttributionSpecChange}
              data={attributionSpecOptions}
            />
          )}
          {canAttributeObjective ? (
            <Text size="xs" c="dimmed">
              This setting determines the events and timeframe used to attribute user behavior to this ad.
            </Text>
          ) : (
            <Text size="xs" c="dimmed" fw={700}>
              You will not be able to use attribution for the objective you have selected.
            </Text>
          )}
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Daily Budget
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <NumberInput
            name="dailyBudget"
            min={10}
            value={facebookAd.dailyBudget || ''}
            placeholder="e.g. 500"
            onChange={(value) => setFacebookAd({ ...facebookAd, dailyBudget: value })}
            leftSection={adAccountCurrencySymbol}
          />
          <Text size="xs" c="dimmed">
            This controls the average daily spend. See{' '}
            <Anchor href="https://www.facebook.com/business/help/190490051321426" target="_blank">
              this article
            </Anchor>{' '}
            for more information. <br />
            <Text component="span" fw={700}>
              Note:
            </Text>{' '}
            this budget is shared across all campaigns which use this ad.
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Bid Cap
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <NumberInput
            name="bidAmount"
            min={6}
            value={facebookAd.bidAmount || ''}
            placeholder="e.g. 5"
            onChange={(value) => setFacebookAd({ ...facebookAd, bidAmount: value })}
            leftSection={adAccountCurrencySymbol}
          />
          <Text size="xs" c="dimmed">
            This is the maximum bid you want to pay for a result based on your optimization goal.{' '}
            <Text component="span" fw={700}>
              Leave blank if unsure
            </Text>
            .
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        <Grid.Col span={3}>
          <Text size="sm" fw={500}>
            Duration (Days)
          </Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <SegmentedControl
            value={durationOption ?? ''}
            onChange={handleDurationOptionChange}
            data={[
              { label: 'Time Framed', value: 'timeFramed' },
              { label: 'Unlimited Time', value: 'unlimitedTime' },
            ]}
          />
          {durationOption === 'unlimitedTime' && (
            <Text size="xs" c="dimmed">
              Your Facebook Ad will be created without a specific end time.{' '}
              <Text component="span" fw={700}>
                Note that your set budget can affect the duration
              </Text>
              .
            </Text>
          )}
          {durationOption === 'timeFramed' && (
            <LabeledSlider
              mt="sm"
              min={1}
              max={31}
              step={1}
              value={facebookAd.durationInDays ? facebookAd.durationInDays : 7}
              onChange={(durationInDays) => setFacebookAd({ ...facebookAd, durationInDays })}
            />
          )}
        </Grid.Col>
      </Grid>

      <Group justify="flex-end" gap="xs" py="md">
        <Button variant="default" onClick={onBack}>
          Back
        </Button>
        {deleteButton}
        {duplicateButton}
        {showUserFlow && (
          <Button
            variant="default"
            onClick={() => showUserFlow()}
            disabled={
              !facebookAd.name ||
              !facebookAd.objective ||
              !facebookAd.dailyBudget ||
              !facebookAd.facebookProductSetId ||
              !durationOption
            }
          >
            <IconMail size={14} /> Send
          </Button>
        )}
        <LockableButton
          onClick={() => saveFacebookAd(facebookAd)}
          restrictionKey={`facebookAds${facebookAd.id ? 'Update' : 'Create'}`}
          disabled={
            !facebookAd.name ||
            !facebookAd.objective ||
            !facebookAd.dailyBudget ||
            (facebookAd.promotedVisual === 'carousel' && !facebookAd?.facebookProductSetId) ||
            (facebookAd.promotedVisual === 'image' && !facebookAd?.imageUrl) ||
            !durationOption ||
            (facebookAd?.objective === 'OUTCOME_APP_PROMOTION' && !facebookAd.appUrl) ||
            (facebookAd?.objective === 'OUTCOME_APP_PROMOTION' && !facebookAd.appId)
          }
          data-tour="facebookAdSaveBtn"
        >
          <IconDeviceFloppy size={14} /> Save
        </LockableButton>
        <ConfirmationModal
          error={deleteError}
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => {
            setDeleteError(undefined);
            deleteFacebookAd(cb).catch(setDeleteError);
          }}
          message={<WarningMessageOnDelete type="Facebook Ad" />}
        />
      </Group>
    </Card>
  );
};

export default FacebookAdForm;
